import React, { useState, ReactNode, FC, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import type { Theme } from '../theme';
import NavBar from './NavBar';
import TopBar from './TopBar';

interface DashboardLayoutProps {
  children: ReactNode;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    display: 'flex',
    height: '100%',
    overflow: 'hidden',
    width: '100%'
  },
  wrapper: {
    display: 'flex',
    flex: '1 1 auto',
    overflow: 'hidden',
    paddingTop: 64,
    [theme.breakpoints.up('lg')]: {
      paddingLeft: 256
    }
  },
  contentContainer: {
    display: 'flex',
    flex: '1 1 auto',
    overflow: 'hidden'
  },
  content: {
    flex: '1 1 auto',
    height: '100%',
    overflow: 'auto'
  }
}));

const DashboardLayout: FC<DashboardLayoutProps> = ({ children }: DashboardLayoutProps) => {
  const classes = useStyles();
  const [open, setOpen] = useState<boolean>(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const wrapperStyle = useMemo(
    () => ({
      paddingLeft: isMobile ? (open ? 0 : 0) : open ? 256 : 0,
      transition: 'padding-left 0.4s ease-in-out'
    }),
    [open, isMobile]
  );

  const handleDrawerClose = () => {
    setOpen(false);
  };
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  useEffect(() => {
    if(isMobile){
      setOpen(false);
    }
  }, [isMobile, location.pathname]);

  return (
    <div className={classes.root}>
      <TopBar handleDrawerOpen={handleDrawerOpen} openInitial={open} isMobile={isMobile} />
      <NavBar handleDrawerClose={handleDrawerClose} openInitial={open} />
      <div className={classes.wrapper} style={wrapperStyle}>
        <div className={classes.contentContainer}>
          <div className={classes.content}>{children}</div>
        </div>
      </div>
    </div>
  );
};

DashboardLayout.propTypes = {
  children: PropTypes.node
};

export default DashboardLayout;
